import { useState, useMemo } from "react"
import { X, Search, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface DescriptorChild {
  key: string
  label: string
}

interface DescriptorCategory {
  key: string
  label: string
  color: string
  children: DescriptorChild[]
}

// SCA flavor wheel-aligned colors
export const DESCRIPTOR_CATEGORIES: DescriptorCategory[] = [
  { key: "floral", label: "Floral", color: "#E8A0BF", children: [] },
  {
    key: "fruity",
    label: "Frutal",
    color: "#D94F4F",
    children: [
      { key: "berry", label: "Bayas" },
      { key: "dried_fruit", label: "Fruta seca" },
      { key: "citrus_fruit", label: "Cítricos" },
    ],
  },
  {
    key: "sour_fermented",
    label: "Ácido/Fermentado",
    color: "#E8C547",
    children: [
      { key: "sour", label: "Ácido" },
      { key: "fermented", label: "Fermentado" },
    ],
  },
  {
    key: "green_vegetative",
    label: "Verde/Vegetativo",
    color: "#7CB342",
    children: [],
  },
  {
    key: "other",
    label: "Otro",
    color: "#90A4AE",
    children: [
      { key: "chemical", label: "Químico" },
      { key: "musty_earthy", label: "Mohoso/Terroso" },
      { key: "papery", label: "Papel" },
      { key: "woody", label: "Madera" },
    ],
  },
  {
    key: "roasted",
    label: "Tostado",
    color: "#8D6E63",
    children: [
      { key: "cereal", label: "Cereal" },
      { key: "burnt", label: "Quemado" },
      { key: "tobacco", label: "Tabaco" },
    ],
  },
  {
    key: "nutty_cocoa",
    label: "Nuez/Cacao",
    color: "#A1887F",
    children: [
      { key: "nutty", label: "Nuez" },
      { key: "cocoa", label: "Cacao" },
    ],
  },
  { key: "spice", label: "Especias", color: "#FF8A65", children: [] },
  {
    key: "sweet",
    label: "Dulce",
    color: "#FFB74D",
    children: [
      { key: "vanilla_vanillin", label: "Vainilla" },
      { key: "brown_sugar", label: "Azúcar morena" },
    ],
  },
]

// Flatten all selectable items with their parent color
function getAllDescriptors() {
  const items: {
    key: string
    label: string
    color: string
    parentLabel: string
  }[] = []
  for (const cat of DESCRIPTOR_CATEGORIES) {
    items.push({
      key: cat.key,
      label: cat.label,
      color: cat.color,
      parentLabel: cat.label,
    })
    for (const child of cat.children) {
      items.push({
        key: child.key,
        label: child.label,
        color: cat.color,
        parentLabel: cat.label,
      })
    }
  }
  return items
}

const ALL_DESCRIPTORS = getAllDescriptors()

export function getDescriptorColor(key: string): string {
  for (const cat of DESCRIPTOR_CATEGORIES) {
    if (cat.key === key) return cat.color
    for (const child of cat.children) {
      if (child.key === key) return cat.color
    }
  }
  return "#9E9E9E"
}

export function getDescriptorLabel(key: string): string {
  for (const cat of DESCRIPTOR_CATEGORIES) {
    if (cat.key === key) return cat.label
    for (const child of cat.children) {
      if (child.key === key) return child.label
    }
  }
  return key
}

interface DescriptorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selected: string[]
  onApply: (selected: string[]) => void
  maxSelections: number
  title?: string
}

export function DescriptorModal({
  open,
  onOpenChange,
  selected: initialSelected,
  onApply,
  maxSelections,
  title = "Descriptores",
}: DescriptorModalProps) {
  const [localSelected, setLocalSelected] = useState<string[]>(initialSelected)
  const [search, setSearch] = useState("")
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  // Reset local state when modal opens
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setLocalSelected(initialSelected)
      setSearch("")
      setActiveCategory(null)
    }
    onOpenChange(isOpen)
  }

  const filteredDescriptors = useMemo(() => {
    let items = ALL_DESCRIPTORS
    if (activeCategory) {
      const cat = DESCRIPTOR_CATEGORIES.find((c) => c.key === activeCategory)
      if (cat) {
        const keys = [cat.key, ...cat.children.map((c) => c.key)]
        items = items.filter((d) => keys.includes(d.key))
      }
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter((d) => d.label.toLowerCase().includes(q))
    }
    return items
  }, [search, activeCategory])

  const toggle = (key: string) => {
    if (localSelected.includes(key)) {
      setLocalSelected(localSelected.filter((k) => k !== key))
    } else if (localSelected.length < maxSelections) {
      setLocalSelected([...localSelected, key])
    }
  }

  const atLimit = localSelected.length >= maxSelections

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {/* Selected descriptors */}
        {localSelected.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium">
              Seleccionados ({localSelected.length}/{maxSelections})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {localSelected.map((key) => {
                const color = getDescriptorColor(key)
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggle(key)}
                    className="flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-white transition-opacity hover:opacity-80"
                    style={{ backgroundColor: color }}
                  >
                    {getDescriptorLabel(key)}
                    <X className="h-3 w-3" />
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar descriptores..."
            className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring w-full rounded-md border py-2 pr-3 pl-9 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          />
        </div>

        {/* Descriptor grid */}
        <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
          <div className="flex flex-wrap gap-1.5 py-2">
            {filteredDescriptors.map((desc) => {
              const isSelected = localSelected.includes(desc.key)
              const isParent = DESCRIPTOR_CATEGORIES.some(
                (c) => c.key === desc.key,
              )
              return (
                <button
                  key={desc.key}
                  type="button"
                  onClick={() => toggle(desc.key)}
                  disabled={atLimit && !isSelected}
                  className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-all ${
                    isSelected
                      ? "border-transparent text-white"
                      : atLimit
                        ? "border-muted text-muted-foreground cursor-not-allowed opacity-40"
                        : "border-transparent text-white/90 hover:opacity-80"
                  } ${isParent ? "font-semibold" : ""}`}
                  style={{
                    backgroundColor: isSelected
                      ? desc.color
                      : atLimit && !isSelected
                        ? undefined
                        : `${desc.color}60`,
                    ...(atLimit && !isSelected ? {} : {}),
                  }}
                >
                  {desc.label}
                </button>
              )
            })}
            {filteredDescriptors.length === 0 && (
              <p className="text-muted-foreground w-full py-4 text-center text-sm">
                No se encontraron descriptores
              </p>
            )}
          </div>
        </div>

        {/* Category filter chips */}
        <div className="border-t pt-3">
          <div className="flex flex-wrap gap-1.5">
            {DESCRIPTOR_CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                type="button"
                onClick={() =>
                  setActiveCategory(activeCategory === cat.key ? null : cat.key)
                }
                className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all ${
                  activeCategory === cat.key
                    ? "border-transparent text-white"
                    : "text-foreground/80 border-transparent"
                }`}
                style={{
                  backgroundColor:
                    activeCategory === cat.key ? cat.color : `${cat.color}30`,
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setLocalSelected([])}
          >
            Limpiar
          </Button>
          <Button
            type="button"
            className="bg-primary hover:bg-primary/90"
            onClick={() => {
              onApply(localSelected)
              onOpenChange(false)
            }}
          >
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Helper button to open the modal, with selected tags display
export function DescriptorPicker({
  selected,
  onApply,
  maxSelections,
  title,
}: {
  selected: string[]
  onApply: (selected: string[]) => void
  maxSelections: number
  title?: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-primary -ml-2"
        onClick={() => setOpen(true)}
      >
        <Plus className="mr-1 h-3 w-3" />
        Agregar descriptores (max {maxSelections})
      </Button>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((key) => {
            const color = getDescriptorColor(key)
            return (
              <span
                key={key}
                className="rounded-full px-2.5 py-0.5 text-xs font-medium text-white"
                style={{ backgroundColor: color }}
              >
                {getDescriptorLabel(key)}
              </span>
            )
          })}
        </div>
      )}

      {selected.length === 0 && (
        <p className="text-muted-foreground text-xs italic">
          Haz clic en agregar para seleccionar descriptores
        </p>
      )}

      <DescriptorModal
        open={open}
        onOpenChange={setOpen}
        selected={selected}
        onApply={onApply}
        maxSelections={maxSelections}
        title={title}
      />
    </div>
  )
}
